import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`;

const SUGGESTIONS = [
  'What makes InkPhD different from ChatGPT?',
  'How do I start a dissertation?',
  'Which plan do I need?',
  'How are citations verified?',
];

/** Floating product-support assistant, available on every page. */
export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([{
    role: 'assistant',
    content: 'Hello — I am the InkPhD assistant. Ask me anything about the workflow, '
      + 'the modules, citations, plans or compliance.',
  }]);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || busy) return;
    setInput('');
    const history = [...messages, { role: 'user', content: question }];
    setMessages(history);
    setBusy(true);
    try {
      const { data } = await axios.post(API, {
        message: question, session_id: sessionId, history: history.slice(-6),
      });
      setSessionId(data.session_id);
      setMessages([...history, { role: 'assistant', content: data.answer }]);
    } catch {
      setMessages([...history, {
        role: 'assistant',
        content: 'I could not reach the assistant just now. Please try again, or use the '
          + 'contact form at the bottom of the page.',
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-toggle"
        aria-label={open ? 'Close assistant' : 'Ask the InkPhD assistant'}
        className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full bg-[#0b1a35] text-white
          shadow-xl flex items-center justify-center transition-transform hover:scale-105
          hover:bg-[#1d4ed8] print:hidden"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-24 right-4 sm:right-5 z-[80] w-[min(400px,calc(100vw-2rem))]
            max-h-[70vh] rounded-2xl border border-border bg-card shadow-2xl flex flex-col
            overflow-hidden print:hidden"
        >
          <div className="px-4 py-3 border-b border-border bg-[#0b1a35] text-white">
            <p className="text-sm font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> InkPhD Assistant
            </p>
            <p className="text-[11px] opacity-80">
              Product questions only — your manuscripts are never read here.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="chat-widget-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'text-right' : ''}>
                <div className={`inline-block max-w-[90%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed
                  whitespace-pre-wrap ${m.role === 'user'
                    ? 'bg-[#1d4ed8] text-white rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            <div ref={endRef} />
          </div>

          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGGESTIONS.map((s, i) => (
                <button key={s} onClick={() => send(s)} data-testid={`chat-widget-suggestion-${i}`}
                  className="text-[11px] rounded-full border border-border px-2.5 py-1
                    hover:border-[#1d4ed8]/50 transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-border p-2 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about InkPhD…"
              data-testid="chat-widget-input"
              className="flex-1 h-9 px-3 text-sm rounded-full bg-background border border-input
                focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]/40"
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-widget-send"
              className="w-9 h-9 rounded-full bg-[#0b1a35] hover:bg-[#1d4ed8] text-white
                flex items-center justify-center disabled:opacity-50 transition-colors">
              <Send className="w-4 h-4" />
            </button>
          </form>
          <p className="px-3 pb-2 text-[10px] text-muted-foreground">
            Answers are AI-generated and can be wrong. For anything binding use the{' '}
            <Link to="/contact" className="underline">contact form</Link>.
          </p>
        </div>
      )}
    </>
  );
}
